import { __rest } from './../../external/tslib/tslib.es6.js';
import React, { forwardRef, useEffect } from 'react';
import { SelectCategory } from './SelectCategory.js';
import { useSelect, setPreviousSelectedOptions, clearFilteredOptions, unselectAllOptions, registerOption } from './SelectContext.js';
import { OptionContent, SelectOption } from './SelectOption.js';
import { StyledDropdown, ClearSearch, EmptyStateMessage } from './styles.js';
import { Flex } from '../Flex/Flex.js';
import Skeleton from '../Skeleton/Skeleton.js';
import Caption from '../Text/Caption/Caption.js';
import '../Text/DetailedSubheading/DetailedSubheading.js';
import '../Text/OverlineLg/OverlineLg.js';
import '../Text/OverlineMd/OverlineMd.js';
import '../Text/ParagraphLg/ParagraphLg.js';
import ParagraphMd from '../Text/ParagraphMd/ParagraphMd.js';
import '../Text/SectionHeading/SectionHeading.js';
import '../Text/SectionSubheading/SectionSubheading.js';
import '../Text/Title/Title.js';

const SELECT_DROPDOWN_HEIGHT = '304px';
const SELECT_OPTION_ID_PREFIX = 'olist-select-option-';
const registerSelectOptions = ({ children: rootChildren, dispatch, }) => {
    React.Children.map(rootChildren, (child) => {
        const processChild = (child, label) => {
            if (!React.isValidElement(child)) {
                return;
            }
            if (typeof child.type === 'string') {
                return;
            }
            if (child.type === SelectOption) {
                const { value, children, selected = false, id, } = child.props;
                const optionId = id || `${SELECT_OPTION_ID_PREFIX}${value}`;
                registerOption(dispatch, {
                    value,
                    text: children,
                    selected,
                    category: label,
                    id: optionId,
                });
            }
        };
        processChild(child, undefined);
        const childElement = child;
        if (childElement.type === SelectCategory) {
            const { label, children } = childElement.props;
            React.Children.map(children, (child) => {
                processChild(child, label);
            });
        }
    });
};
const SelectDropdown = forwardRef((_a, ref) => {
    var { isOpen, anchorElement, onClose, children, isLoading } = _a, props = __rest(_a, ["isOpen", "anchorElement", "onClose", "children", "isLoading"]);
    const [{ multi, previousSelectedOptions, selectedOptions, isSearched, filteredOptions, options, }, dispatch,] = useSelect();
    const hasCategory = options.some((option) => Boolean(option.category));
    const shouldRenderPreviousSelectedOptionsDivider = !hasCategory && options.length !== (previousSelectedOptions === null || previousSelectedOptions === void 0 ? void 0 : previousSelectedOptions.length);
    const hasSelectedItems = selectedOptions && selectedOptions.length > 0;
    const plural = selectedOptions && selectedOptions.length > 1;
    const shouldDisplayEmptyStateMessage = isSearched && !filteredOptions.length && !(previousSelectedOptions === null || previousSelectedOptions === void 0 ? void 0 : previousSelectedOptions.length);
    const messages = {
        plural: `${selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.length} itens selecionados`,
        singular: `${selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.length} item selecionado`,
    };
    const message = plural ? messages.plural : messages.singular;
    const handleClearSearch = () => {
        clearFilteredOptions(dispatch);
        unselectAllOptions(dispatch);
    };
    const handleClose = () => {
        onClose === null || onClose === void 0 ? void 0 : onClose();
        clearFilteredOptions(dispatch);
        multi && setPreviousSelectedOptions(dispatch);
    };
    useEffect(() => {
        registerSelectOptions({ children, dispatch });
        multi && setPreviousSelectedOptions(dispatch);
    }, [children]);
    if (isLoading) {
        return (React.createElement(StyledDropdown, Object.assign({ isOpen: isOpen, anchorElement: anchorElement, role: "listbox", onClose: handleClose, maxHeight: SELECT_DROPDOWN_HEIGHT, minHeight: SELECT_DROPDOWN_HEIGHT }, props), Array.from({ length: 5 }).map((_, index) => (React.createElement("li", { key: index },
            React.createElement(Skeleton, { mt: "8px", width: "100%", height: "44px" }))))));
    }
    return (React.createElement(StyledDropdown, Object.assign({ ref: ref, isOpen: isOpen, anchorElement: anchorElement, onClose: handleClose, role: "listbox", maxHeight: SELECT_DROPDOWN_HEIGHT, minHeight: SELECT_DROPDOWN_HEIGHT }, props),
        multi && hasSelectedItems && (React.createElement(Flex, { justifyContent: "space-between", as: "li" },
            React.createElement(Caption, { marginLeft: "inline.2xsmall", marginTop: "stack.1xsmall", marginBottom: "stack.1xsmall" }, message),
            React.createElement(ClearSearch, { forwardedAs: "button", onClick: handleClearSearch }, "limpar sele\u00E7\u00E3o"))),
        previousSelectedOptions && Boolean(previousSelectedOptions.length) && (React.createElement(React.Fragment, null,
            previousSelectedOptions.map((option) => (React.createElement(OptionContent, { key: option.value, option: option }))),
            shouldRenderPreviousSelectedOptionsDivider && (React.createElement(StyledDropdown.Divider, null)))),
        shouldDisplayEmptyStateMessage && (React.createElement(EmptyStateMessage, null,
            React.createElement(ParagraphMd, null, "Nenhum resultado encontrado."))),
        children));
});
SelectDropdown.displayName = 'SelectDropdown';

export { SelectDropdown };
